import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import CancelIcon from '@material-ui/icons/Cancel';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import StorageIcon from '@material-ui/icons/Storage';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3)
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: 600
  },
  stepper: {
    backgroundColor: 'transparent'
  },
  eventTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontFamily: 'monospace'
  },
  eventDescription: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary
  },
  eventMetadata: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    fontSize: '0.75rem',
    fontFamily: 'monospace'
  },
  durationBadge: {
    marginLeft: theme.spacing(1),
    fontSize: '0.7rem'
  }
}));

const getEventIcon = (eventType) => {
  const icons = {
    MATCH_CREATED: <AddCircleIcon />,
    AGENT_ASSIGNED: <StorageIcon />,
    PLAYER_CONNECTED: <PersonAddIcon />,
    MATCH_STARTED: <PlayArrowIcon />,
    MATCH_FINISHED: <CheckCircleIcon />,
    MATCH_CANCELLED: <CancelIcon />,
    STATE_TRANSITION: <ArrowForwardIcon />,
    ERROR: <ErrorIcon />
  };
  
  return icons[eventType] || <ArrowForwardIcon />;
};

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit',
    hour12: false 
  });
};

const formatDuration = (startTime, endTime) => {
  const durationMs = new Date(endTime) - new Date(startTime);
  const seconds = Math.floor(durationMs / 1000);
  
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
};

const MatchTimeline = ({ timeline = [], matchId }) => {
  const classes = useStyles();
  
  if (timeline.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.title}>
          Match Timeline
        </Typography>
        <Typography color="textSecondary" align="center">
          No timeline data available
        </Typography>
      </Paper>
    );
  }
  
  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.title}>
        Match Timeline
        <Chip 
          label={`${timeline.length} events`}
          size="small"
          className={classes.durationBadge}
        />
      </Typography>
      
      <Stepper orientation="vertical" className={classes.stepper}>
        {timeline.map((event, index) => {
          const duration = index > 0 
            ? formatDuration(timeline[index - 1].timestamp, event.timestamp)
            : null;
          
          return (
            <Step key={index} active={true} completed={true}>
              <StepLabel
                icon={getEventIcon(event.eventType)}
                optional={
                  <Typography className={classes.eventTime}>
                    {formatTimestamp(event.timestamp)}
                    {duration && ` (+${duration})`}
                  </Typography>
                }
              >
                {event.title}
              </StepLabel>
              <StepContent>
                {event.description && (
                  <Typography className={classes.eventDescription}>
                    {event.description}
                  </Typography>
                )}
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    </Paper>
  );
};

export default MatchTimeline;

